import type { Snapshot, SyncData } from "./sync";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";

/** 401 时抛出，由上层引导重新登录 */
export class SessionExpiredError extends Error {
  constructor() {
    super("SESSION_EXPIRED");
    this.name = "SessionExpiredError";
  }
}

let getTokenFn: (() => Promise<string | null>) | null = null;

/** 注入 Supabase token 获取函数（登录后由 useSync 设置） */
export function setTokenProvider(fn: () => Promise<string | null>): void {
  getTokenFn = fn;
}

/** 带 Bearer token 的 fetch 封装；401 → SessionExpiredError */
export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = getTokenFn ? await getTokenFn() : null;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (response.status === 401) throw new SessionExpiredError();
  return response;
}

/** 拉取云端快照；无数据或出错返回 null */
export async function pullSnapshot(): Promise<Snapshot | null> {
  try {
    const res = await apiFetch("/snapshot");
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: SyncData } | null;
    return json?.data ? (json as Snapshot) : null;
  } catch {
    return null;
  }
}

/** 推送本地快照到云端（last-write-wins） */
export async function pushSnapshot(data: SyncData): Promise<boolean> {
  try {
    const clientTs = new Date().toISOString();
    const res = await apiFetch("/snapshot", {
      method: "PUT",
      body: JSON.stringify({ clientTs, data }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** 通过 worker 抓取 URL 文本（Task 9.3 端点） */
export async function importUrl(url: string): Promise<{ text: string } | null> {
  try {
    const res = await apiFetch("/import-url", {
      method: "POST",
      body: JSON.stringify({ url }),
    });
    if (!res.ok) return null;
    return (await res.json()) as { text: string };
  } catch {
    return null;
  }
}
